import { AxiosError } from 'axios';
import { apiClient } from '@/lib/api-client';
import type { AdminUserListItem, ApiResponse, ResetPasswordResponse } from '@/types/api';

/**
 * Thrown when the API returns a 409 from a user-mutation endpoint
 * (e.g. `DUPLICATE_EMAIL`, `LAST_ADMIN`). Carries the server-side message
 * so the calling component can render it inline.
 */
export class ConflictError extends Error {
  readonly errorCode?: string;
  constructor(message: string, errorCode?: string) {
    super(message);
    this.name = 'ConflictError';
    this.errorCode = errorCode;
  }
}

/**
 * Fetch all users (active + inactive) for the admin management view.
 * Backend: `GET /api/users` (admin-only).
 */
export async function listUsers(): Promise<AdminUserListItem[]> {
  const response = await apiClient.get<ApiResponse<AdminUserListItem[]>>('/api/users');
  return response.data.data ?? [];
}

export interface CreateUserPayload {
  email: string;
  firstName: string;
  lastName: string;
  role: 'Admin' | 'User';
}

export interface CreatedUserResponse {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  role: 'Admin' | 'User';
  initialPassword: string;
}

export interface UpdateUserPayload {
  firstName?: string;
  lastName?: string;
  role?: 'Admin' | 'User';
  isActive?: boolean;
}

/**
 * Create a new user via `POST /api/users` (admin-only). Returns the created
 * user along with the server-generated initial password — this is the only
 * place it is ever surfaced. Throws {@link ConflictError} on 409 (duplicate
 * email); re-throws other axios errors unchanged.
 */
export async function createUser(payload: CreateUserPayload): Promise<CreatedUserResponse> {
  try {
    const response = await apiClient.post<ApiResponse<CreatedUserResponse>>('/api/users', payload);
    const data = response.data.data;
    if (!data) {
      throw new Error(response.data.message ?? 'Create user response missing data.');
    }
    return data;
  } catch (err) {
    if (err instanceof AxiosError && err.response?.status === 409) {
      const body = err.response.data as ApiResponse<unknown> | undefined;
      const message = body?.message ?? body?.errors?.[0] ?? 'Email already in use.';
      throw new ConflictError(message, 'DUPLICATE_EMAIL');
    }
    throw err;
  }
}

/**
 * Update a user via `PUT /api/users/{id}` (admin-only). Only fields present
 * on the payload are sent. Throws {@link ConflictError} on 409 (e.g.
 * `LAST_ADMIN` — demoting/deactivating the sole active Admin); re-throws
 * other axios errors unchanged.
 */
export async function updateUser(id: number, payload: UpdateUserPayload): Promise<void> {
  try {
    await apiClient.put<ApiResponse<unknown>>(`/api/users/${id}`, payload);
  } catch (err) {
    if (err instanceof AxiosError && err.response?.status === 409) {
      const body = err.response.data as ApiResponse<unknown> | undefined;
      const message =
        body?.message ?? body?.errors?.[0] ?? 'At least one active Admin must remain.';
      throw new ConflictError(message, 'LAST_ADMIN');
    }
    throw err;
  }
}

/**
 * Reset a user's password via `POST /api/users/{id}/reset-password` (admin-only).
 * Returns the new one-time password to be revealed exactly once in the UI.
 * Throws {@link ConflictError} on 409 (e.g. admin tries to reset their own
 * password — the UI hides the button but we surface the server error too for
 * defence in depth). The server sets `Cache-Control: no-store, no-cache` on
 * this response so the new password is not retained in any HTTP cache.
 */
export async function resetUserPassword(id: number): Promise<ResetPasswordResponse> {
  try {
    const response = await apiClient.post<ApiResponse<ResetPasswordResponse>>(
      `/api/users/${id}/reset-password`,
    );
    const data = response.data.data;
    if (!data) {
      throw new Error(response.data.message ?? 'Reset password response missing data.');
    }
    return data;
  } catch (err) {
    if (err instanceof AxiosError && err.response?.status === 409) {
      const body = err.response.data as ApiResponse<unknown> | undefined;
      const message =
        body?.message ?? body?.errors?.[0] ?? 'Cannot reset your own password.';
      throw new ConflictError(message, 'SELF_RESET');
    }
    throw err;
  }
}

/**
 * Deactivate a user via `DELETE /api/users/{id}` (admin-only soft-delete).
 * Throws {@link ConflictError} on 409 (e.g. `LAST_ADMIN` — deactivating the
 * sole active Admin); re-throws other axios errors unchanged.
 */
export async function deactivateUser(id: number): Promise<void> {
  try {
    await apiClient.delete<ApiResponse<unknown>>(`/api/users/${id}`);
  } catch (err) {
    if (err instanceof AxiosError && err.response?.status === 409) {
      const body = err.response.data as ApiResponse<unknown> | undefined;
      const message =
        body?.message ?? body?.errors?.[0] ?? 'At least one active Admin must remain.';
      throw new ConflictError(message, 'LAST_ADMIN');
    }
    throw err;
  }
}
